import { Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import useSWR from "swr";
import { DEFAULT_CHECKOUT_COPY, type CheckoutCopy } from "@/shared/checkout-copy";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import { PageHeader } from "@/web/components/page-header";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";
import { Textarea } from "@/web/components/ui/textarea";

interface NameRule {
  match: string;
  display: string;
}

interface SettingsResponse {
  checkout_copy: CheckoutCopy;
  product_name_rules: NameRule[];
}

interface FieldSpec {
  key: keyof CheckoutCopy;
  label: string;
  hint?: string;
  multiline?: boolean;
}

const GROUPS: Array<{ title: string; description: string; fields: FieldSpec[] }> = [
  {
    title: "品牌区",
    description: "收银台顶部的品牌展示。",
    fields: [
      { key: "brand_name", label: "品牌名称" },
      { key: "brand_tagline", label: "品牌副标题" },
    ],
  },
  {
    title: "金额提示",
    description: "仅当实付金额与订单金额不一致（分位匹配）时显示。",
    fields: [
      { key: "surcharge_lead", label: "提示主句" },
      { key: "surcharge_note", label: "提示补充" },
    ],
  },
  {
    title: "扫码指引",
    description: "按支付方式分别展示。可用占位符 {out_trade_no}。",
    fields: [
      { key: "guide_wxpay", label: "微信支付", multiline: true },
      { key: "guide_alipay_qr", label: "支付宝·扫码", multiline: true },
      { key: "guide_alipay_deeplink", label: "支付宝·一键唤起", multiline: true },
      { key: "guide_transfer", label: "支付宝·转账", multiline: true },
    ],
  },
  {
    title: "微信支付提示",
    description: "仅微信支付时显示。每行一条，可用回车分行。",
    fields: [
      { key: "wxpay_steps", label: "支付步骤（每行一步）", multiline: true },
      { key: "wxpay_notice", label: "注意事项（每行一条）", multiline: true },
      { key: "wxpay_mobile_tip", label: "手机端提示", multiline: true },
    ],
  },
  {
    title: "状态提示",
    description: "订单状态变化时展示的文案。",
    fields: [
      { key: "aside_note", label: "侧栏提示", multiline: true },
      { key: "expired_note", label: "超时确认中提示", multiline: true },
      { key: "ended_title", label: "订单已结束·标题" },
      { key: "ended_note", label: "订单已结束·说明", multiline: true },
    ],
  },
  {
    title: "支付成功页",
    description: "支付完成后的展示。可用占位符 {out_trade_no}；「自动返回提示」还可用 {seconds} 表示剩余秒数。",
    fields: [
      { key: "paid_title", label: "标题" },
      { key: "paid_note", label: "说明", multiline: true },
      { key: "paid_return", label: "返回按钮文字" },
      { key: "paid_redirect", label: "自动返回提示" },
    ],
  },
  {
    title: "订单信息区",
    description: "右侧信息栏的标题与字段标签。",
    fields: [
      { key: "aside_title", label: "区域标题" },
      { key: "label_trade_no", label: "字段：订单号" },
      { key: "label_created_at", label: "字段：创建时间" },
      { key: "label_remaining", label: "字段：自动确认剩余" },
    ],
  },
  {
    title: "移动端按钮",
    description: "手机上显示的直达按钮文案。",
    fields: [
      { key: "button_open_wxpay", label: "微信按钮" },
      { key: "button_open_alipay", label: "支付宝按钮" },
    ],
  },
];

export function CheckoutCopyPage() {
  const { data, isLoading, mutate } = useSWR<SettingsResponse>("/admin-api/settings", swrFetcher);
  const [copy, setCopy] = useState<CheckoutCopy>(DEFAULT_CHECKOUT_COPY);
  const [rules, setRules] = useState<NameRule[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.checkout_copy) setCopy(data.checkout_copy);
    if (data?.product_name_rules) setRules(data.product_name_rules);
  }, [data]);

  function update(key: keyof CheckoutCopy, value: string) {
    setCopy((prev) => ({ ...prev, [key]: value }));
  }

  function resetOne(key: keyof CheckoutCopy) {
    update(key, DEFAULT_CHECKOUT_COPY[key]);
  }

  function resetAll() {
    setCopy(DEFAULT_CHECKOUT_COPY);
    toast.message("已恢复为默认文案，记得点保存");
  }

  async function save() {
    setSaving(true);
    try {
      await apiFetch("/admin-api/settings", { method: "PUT", ...jsonBody({ checkout_copy: copy, product_name_rules: rules }) });
      toast.success("已保存，收银台立即生效");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <Loading label="正在读取文案配置" />;

  return (
    <>
      <PageHeader
        title="收银台文案"
        description="自定义收银台上展示的所有提示文字，保存后立即生效。留空时会回落到默认文案。"
        actions={
          <>
            <Button variant="ghost" onClick={resetAll}><RotateCcw />全部恢复默认</Button>
            <Button onClick={save} disabled={saving}><Save />{saving ? "正在保存…" : "保存"}</Button>
          </>
        }
      />

      <div className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle>商品名替换</CardTitle>
            <CardDescription>
              商户传来的商品名<strong>包含</strong>「匹配文本」时，换成「显示模板」。
              模板里可用 <code className="font-mono">{'{{amount}}'}</code> 表示订单金额。
              未匹配的商品名保持原样。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rules.length === 0 ? (
              <p className="text-xs leading-5 text-muted">尚未配置规则，收银台会原样显示商户传来的商品名。</p>
            ) : null}
            {rules.map((rule, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <Input
                  value={rule.match}
                  placeholder="TUC"
                  className="w-36"
                  onChange={(event) => setRules((prev) => prev.map((item, i) => (i === index ? { ...item, match: event.target.value } : item)))}
                />
                <span className="text-xs text-muted">替换为</span>
                <Input
                  value={rule.display}
                  placeholder="余额充值{{amount}}元"
                  className="min-w-[180px] flex-1"
                  onChange={(event) => setRules((prev) => prev.map((item, i) => (i === index ? { ...item, display: event.target.value } : item)))}
                />
                <Button variant="ghost" size="sm" onClick={() => setRules((prev) => prev.filter((_, i) => i !== index))}><Trash2 />移除</Button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" disabled={rules.length >= 50} onClick={() => setRules((prev) => [...prev, { match: "", display: "" }])}><Plus />添加一条</Button>
              <span className="text-xs text-muted">最多 50 条，按顺序取第一个命中的</span>
            </div>
            <p className="rounded-md bg-foreground/[0.035] px-4 py-3 text-xs leading-5 text-muted">
              示例：匹配文本填 <code className="font-mono">TUC</code>，显示模板填 <code className="font-mono">{'余额充值{{amount}}元'}</code>，
              则订单商品名 <code className="font-mono">TUC5</code>（金额 5.00）会显示为「余额充值5.00元」。
            </p>
          </CardContent>
        </Card>

        {GROUPS.map((group) => (
          <Card key={group.title}>
            <CardHeader>
              <CardTitle>{group.title}</CardTitle>
              <CardDescription>{group.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {group.fields.map((field) => {
                const value = copy[field.key];
                const isDefault = value === DEFAULT_CHECKOUT_COPY[field.key];
                return (
                  <div key={field.key} className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor={field.key}>{field.label}</Label>
                      {isDefault ? null : (
                        <button
                          type="button"
                          onClick={() => resetOne(field.key)}
                          className="text-xs text-muted transition-colors hover:text-foreground"
                        >
                          恢复默认
                        </button>
                      )}
                    </div>
                    {field.multiline ? (
                      <Textarea
                        id={field.key}
                        value={value}
                        onChange={(event) => update(field.key, event.target.value)}
                        placeholder={DEFAULT_CHECKOUT_COPY[field.key]}
                        className="min-h-20"
                      />
                    ) : (
                      <Input
                        id={field.key}
                        value={value}
                        onChange={(event) => update(field.key, event.target.value)}
                        placeholder={DEFAULT_CHECKOUT_COPY[field.key]}
                      />
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
